/**
 * Firestore Wrapper Service
 * 
 * This service wraps Firestore operations with rate limiting functionality
 * to prevent excessive reads and writes that could lead to high costs.
 * 
 * It also includes detailed logging for debugging purposes.
 */

import { 
  collection, 
  query, 
  where, 
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  DocumentData,
  DocumentReference,
  CollectionReference,
  Query,
  QuerySnapshot,
  DocumentSnapshot,
  Firestore,
  WriteBatch,
  writeBatch,
  runTransaction,
  Transaction,
  orderBy,
  limit as firestoreLimit,
  addDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { rateLimiter } from './rateLimiter';
import { firestoreDebugService } from './firestoreDebugService';

// Enable emergency mode by default in production
// Temporarily disabled to prevent UI freezing during development
// if (process.env.NODE_ENV === 'production') {
//   rateLimiter.enableEmergencyMode();
// }

/**
 * Extract collection name from a path or reference
 */
function getCollectionName(path: any): string {
  try {
    if (typeof path === 'string') {
      // Extract collection name from path string
      const parts = path.split('/');
      // For paths like 'collection/doc/subcollection', we want the last part
      return parts[parts.length % 2 === 0 ? parts.length - 1 : 0];
    } else if (path && path.parent && path.parent.id) {
      // It's a DocumentReference
      return path.parent.id;
    } else if (path && path.id) {
      // It's a CollectionReference
      return path.id;
    } else if (path && path.firestore) {
      // It's a Query, try to extract from path
      const pathString = path.path || '';
      if (pathString) {
        const parts = pathString.split('/');
        return parts[0] || 'unknown';
      }
    }
  } catch (e) {
    console.warn('Error extracting collection name:', e);
  }
  
  // Default fallback
  return 'unknown';
}

/**
 * Wrapped Firestore operations
 */
export const firestoreWrapper = {
  /**
   * Wrapped addDoc function
   */
  addDoc: async <T>(collectionRef: CollectionReference<T>, data: T): Promise<DocumentReference<T>> => {
    // Temporarily disable rate limiting to debug UI freeze issue
    // if (!rateLimiter.canPerformOperation('write', getCollectionName(collectionRef))) {
    //   console.error(`[FirestoreWrapper] Rate limit exceeded for write operation on ${getCollectionName(collectionRef)}`);
    //   throw new Error(`Rate limit exceeded for write operation on ${getCollectionName(collectionRef)}`);
    // }
    
    // Log the operation
    firestoreDebugService.logOperation('write', getCollectionName(collectionRef));
    
    const docRef = doc(collectionRef);
    await setDoc(docRef, data);
    return docRef;
  },

  /**
   * Wrapped collection function
   */
  collection: (path: string): CollectionReference => {
    return collection(db, path);
  },

  /**
   * Wrapped query function
   */
  query: <T = DocumentData>(
    collectionRef: CollectionReference<T>,
    ...queryConstraints: any[]
  ): Query<T> => {
    return query(collectionRef, ...queryConstraints);
  },

  /**
   * Wrapped where function
   */
  where: where,

  /**
   * Wrapped orderBy function
   */
  orderBy: (fieldPath: string, directionStr?: 'asc' | 'desc') => {
    return orderBy(fieldPath, directionStr);
  },

  /**
   * Wrapped limit function
   */
  limit: (limit: number) => {
    return firestoreLimit(limit);
  },

  /**
   * Wrapped getDocs function with rate limiting and logging
   */
  getDocs: async <T = DocumentData>(query: Query<T>): Promise<QuerySnapshot<T>> => {
    const collectionName = getCollectionName(query);
    
    // Temporarily disable rate limiting to debug UI freeze issue
    // if (!rateLimiter.canPerformOperation('read', collectionName)) {
    //   console.error(`[FirestoreWrapper] Rate limit exceeded for read operation on ${collectionName}`);
    //   throw new Error(`Rate limit exceeded for read operation on ${collectionName}`);
    // }
    
    // Log the operation
    firestoreDebugService.logOperation('read', collectionName);
    
    return getDocs(query);
  },

  /**
   * Wrapped doc function
   */
  doc: <T = DocumentData>(
    collectionRef: CollectionReference<T>,
    ...pathSegments: string[]
  ): DocumentReference<T> => {
    return doc(collectionRef, ...pathSegments);
  },

  /**
   * Wrapped getDoc function with rate limiting and logging
   */
  getDoc: async <T = DocumentData>(docRef: DocumentReference<T>): Promise<DocumentSnapshot<T>> => {
    const collectionName = getCollectionName(docRef);
    const docId = docRef.id;
    
    // Temporarily disable rate limiting to debug UI freeze issue
    // if (!rateLimiter.canPerformOperation('read', collectionName)) {
    //   console.error(`[FirestoreWrapper] Rate limit exceeded for read operation on ${collectionName}`);
    //   throw new Error(`Rate limit exceeded for read operation on ${collectionName}`);
    // }
    
    // Log the operation
    firestoreDebugService.logOperation('read', collectionName, docId);
    
    return getDoc(docRef);
  },

  /**
   * Wrapped setDoc function with rate limiting and logging
   */
  setDoc: async <T = DocumentData>(
    docRef: DocumentReference<T>,
    data: T,
    options?: any
  ): Promise<void> => {
    const collectionName = getCollectionName(docRef);
    const docId = docRef.id;
    
    // Temporarily disable rate limiting to debug UI freeze issue
    // if (!rateLimiter.canPerformOperation('write', collectionName)) {
    //   console.error(`[FirestoreWrapper] Rate limit exceeded for write operation on ${collectionName}`);
    //   throw new Error(`Rate limit exceeded for write operation on ${collectionName}`);
    // }
    
    // Log the operation
    firestoreDebugService.logOperation('write', collectionName, docId);
    
    return setDoc(docRef, data, options);
  },

  /**
   * Wrapped updateDoc function with rate limiting and logging
   */
  updateDoc: async <T = DocumentData>(
    docRef: DocumentReference<T>,
    data: any
  ): Promise<void> => {
    const collectionName = getCollectionName(docRef);
    const docId = docRef.id;
    
    // Temporarily disable rate limiting to debug UI freeze issue
    // if (!rateLimiter.canPerformOperation('write', collectionName)) {
    //   console.error(`[FirestoreWrapper] Rate limit exceeded for write operation on ${collectionName}`);
    //   throw new Error(`Rate limit exceeded for write operation on ${collectionName}`);
    // }
    
    // Log the operation
    firestoreDebugService.logOperation('write', collectionName, docId);
    
    return updateDoc(docRef, data);
  },

  /**
   * Wrapped deleteDoc function with rate limiting and logging
   */
  deleteDoc: async <T = DocumentData>(docRef: DocumentReference<T>): Promise<void> => {
    const collectionName = getCollectionName(docRef);
    const docId = docRef.id;
    
    // Temporarily disable rate limiting to debug UI freeze issue
    // if (!rateLimiter.canPerformOperation('delete', collectionName)) {
    //   console.error(`[FirestoreWrapper] Rate limit exceeded for delete operation on ${collectionName}`);
    //   throw new Error(`Rate limit exceeded for delete operation on ${collectionName}`);
    // }
    
    // Log the operation
    firestoreDebugService.logOperation('delete', collectionName, docId);
    
    return deleteDoc(docRef);
  },

  /**
   * Wrapped writeBatch function with logging
   * Note: Individual operations in the batch will be rate limited when committed
   */
  writeBatch: (firestore: Firestore = db): WriteBatch => {
    const batch = writeBatch(firestore);
    
    // Log the batch creation
    firestoreDebugService.logOperation('batch', 'all');
    
    // Return a proxy to intercept and log batch operations
    // This is a simplified approach - in a real implementation, we would
    // intercept all batch methods (set, update, delete) to log them individually
    return batch;
  },

  /**
   * Wrapped runTransaction function with rate limiting and logging
   * Note: This applies a single rate limit check for the entire transaction
   */
  runTransaction: async <T>(
    firestore: Firestore,
    updateFunction: (transaction: Transaction) => Promise<T>
  ): Promise<T> => {
    // Apply a single rate limit check for the transaction
    if (!rateLimiter.canPerformOperation('transaction', 'all')) {
      console.error(`[FirestoreWrapper] Rate limit exceeded for transaction operation`);
      throw new Error(`Rate limit exceeded for transaction operation`);
    }
    
    // Log the operation
    firestoreDebugService.logOperation('transaction', 'all');
    
    return runTransaction(firestore, updateFunction);
  },

  /**
   * Get the rate limiter instance
   */
  getRateLimiter: () => rateLimiter
};
